import axios from 'axios';
import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaSearch, FaPlus, FaTrash, FaCheckCircle, FaTruck, FaTimesCircle, FaEye, FaEdit, FaArrowLeft, FaMapMarkerAlt, FaDirections, FaWhatsapp, FaCopy, FaShareAlt } from 'react-icons/fa';
import AdminPageLoader from './AdminPageLoader';
import OrderNavigationStrip from './OrderNavigationStrip';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

function AdminOrderDetailsPage() {
    const [orderData, setOrderData] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchFocused, setSearchFocused] = useState(false);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updatingStatus, setUpdatingStatus] = useState('');
    const [modifyingItem, setModifyingItem] = useState(null);
    const [showSearch, setShowSearch] = useState(false);
    const [reverifyingPayment, setReverifyingPayment] = useState(false);
    const [showDeliveryPartnerModal, setShowDeliveryPartnerModal] = useState(false);
    const [deliveryPartners, setDeliveryPartners] = useState([]);
    const [selectedDeliveryPartner, setSelectedDeliveryPartner] = useState('');
    const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
    const [allDeliveryPartners, setAllDeliveryPartners] = useState([]);
    const [selectedQuantities, setSelectedQuantities] = useState({});
    const [refreshCounts, setRefreshCounts] = useState(false);
    
    const { id } = useParams();
    const navigate = useNavigate();

    const handleBackButtonClick = () => {
        navigate(-1); // Go back one step in browser history
    };

    const handleApiError = (error) => {
        console.error('API Error:', error);
        alert(error.response?.data?.message || 'Something went wrong. Please try again.');
    };

    const fetchOrderDetails = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE_URL}/order-details/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOrderData(response.data);
            setRefreshCounts(prev => !prev);
        } catch (error) {
            handleApiError(error);
            setTimeout(() => navigate('/admin/orders'), 2000);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async (event) => {
        const value = event.target.value;
        setSearchTerm(value);

        if (value.length > 2) {
            try {
                const token = localStorage.getItem('authToken');
                const response = await axios.get(`${API_BASE_URL}/product-list?search=${value}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setFilteredProducts(response.data || []);
            } catch (error) {
                handleApiError(error);
            }
        } else {
            setFilteredProducts([]);
        }
    };

    const handleIncreaseQuantity = (productId, maxStock) => {
        setSelectedQuantities(prev => {
            const current = prev[productId] || 1;
            if (current >= maxStock) return prev;
            return {
                ...prev,
                [productId]: current + 1
            };
        });
    };

    const handleDecreaseQuantity = (productId) => {
        setSelectedQuantities(prev => {
            const current = prev[productId] || 1;
            if (current <= 1) return prev;
            return {
                ...prev,
                [productId]: current - 1
            };
        });
    };

    const handleAddItem = async (product, quantity) => {
        if (!quantity || quantity <= 0) {
            alert('Please select a valid quantity.');
            return;
        }
        if (!window.confirm(`Add ${quantity} x "${product.name}" to this order?`)) return;

        setModifyingItem(product._id);
        try {
            const token = localStorage.getItem('authToken');
            const payload = {
                _id: product._id,
                name: product.name,
                price: product.price,
                quantity: quantity
            };
            await axios.put(`${API_BASE_URL}/add-item-to-order/${id}`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert('Item added successfully!');
            await fetchOrderDetails();
            setSearchTerm('');
            setFilteredProducts([]);
            setShowSearch(false);
            // Clear the selected quantities dictionary
            setSelectedQuantities(prev => {
                const copy = { ...prev };
                delete copy[product._id];
                return copy;
            });
        } catch (error) {
            handleApiError(error);
        } finally {
            setModifyingItem(null);
        }
    };

    const handleUpdateItemQuantity = async (item, newQuantity) => {
        if (newQuantity < 1) {
            alert('Quantity cannot be less than 1. Use the remove button to delete the item.');
            return;
        }

        setModifyingItem(item._id);
        try {
            const token = localStorage.getItem('authToken');
            const payload = {
                itemId: item._id,
                quantity: newQuantity
            };
            await axios.put(`${API_BASE_URL}/update-item-quantity-in-order/${id}`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            await fetchOrderDetails();
        } catch (error) {
            handleApiError(error);
        } finally {
            setModifyingItem(null);
        }
    };

    const handleRemoveItem = async (item) => {
        if (!window.confirm(`Remove "${item.name}" from this order?`)) return;

        setModifyingItem(item._id);
        try {
            const token = localStorage.getItem('authToken');
            await axios.put(`${API_BASE_URL}/remove-item-from-order/${id}`, item, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert('Item removed successfully!');
            await fetchOrderDetails();
        } catch (error) {
            handleApiError(error);
        } finally {
            setModifyingItem(null);
        }
    };

    const handleUpdateStatus = async (newStatus) => {
        if (newStatus === 'shipped') {
            // Show delivery partner selection modal
            await fetchDeliveryPartners();
            setShowDeliveryPartnerModal(true);
            return;
        }

        if (!window.confirm(`Change order status to "${newStatus.toUpperCase()}"?`)) return;

        setUpdatingStatus(newStatus);
        try {
            const token = localStorage.getItem('authToken');
            await axios.put(`${API_BASE_URL}/update-order-status/${id}`, 
                { status: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            alert(`Order status updated to ${newStatus.toUpperCase()}`);
            await fetchOrderDetails();
        } catch (error) {
            handleApiError(error);
        } finally {
            setUpdatingStatus('');
        }
    };

    const fetchDeliveryPartners = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE_URL}/available-delivery-partners`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDeliveryPartners(response.data || []);
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleAssignAndShip = async () => {
        if (!selectedDeliveryPartner) {
            alert('Please select a delivery partner');
            return;
        }

        setUpdatingStatus('shipped');
        try {
            const token = localStorage.getItem('authToken');
            await axios.put(`${API_BASE_URL}/update-order-status/${id}`, 
                { status: 'shipped', deliveryPartner: selectedDeliveryPartner },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            alert('Order marked as shipped and assigned to delivery partner');
            setShowDeliveryPartnerModal(false);
            setSelectedDeliveryPartner('');
            await fetchOrderDetails();
        } catch (error) {
            handleApiError(error);
        } finally {
            setUpdatingStatus('');
        }
    };

    const handleUpdatePayment = async (newPaymentStatus) => {
        if (!window.confirm(`Mark payment as "${newPaymentStatus.toUpperCase()}"?`)) return;
        try {
            const token = localStorage.getItem('authToken');
            await axios.put(`${API_BASE_URL}/update-payment-status/${id}`, { paymentStatus: newPaymentStatus }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(`Payment status updated to ${newPaymentStatus.toUpperCase()}`);
            await fetchOrderDetails();
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleReverifyPayment = async () => {
        if (reverifyingPayment) return; // guard
        if (!orderData?.merchantOrderId) {
            alert('No merchant order ID to verify.');
            return;
        }
        try {
            setReverifyingPayment(true);
            const token = localStorage.getItem('authToken');
            // Payment status endpoint does not require auth in backend right now, but keep header in case later secured
            const resp = await axios.get(`${API_BASE_URL}/payment-status?merchantOrderId=${orderData.merchantOrderId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const status = resp.data?.status;
            if (status === 'success') {
                // Optionally auto-mark as paid if currently not paid
                if (orderData.paymentStatus !== 'paid') {
                    if (window.confirm('Gateway reports SUCCESS. Update local payment status to PAID?')) {
                        await handleUpdatePayment('paid');
                        return;
                    }
                }
                alert('Gateway Status: SUCCESS');
            } else if (status === 'failed') {
                if (orderData.paymentStatus !== 'failed') {
                    if (window.confirm('Gateway reports FAILED. Update local payment status to FAILED?')) {
                        await handleUpdatePayment('failed');
                        return;
                    }
                }
                alert('Gateway Status: FAILED');
            } else if (status === 'pending') {
                alert('Gateway Status: PENDING');
            } else {
                alert('Gateway Status: UNKNOWN');
            }
        } catch (err) {
            console.error('Re-verify payment error:', err);
            alert('Could not re-verify payment.');
        } finally {
            setReverifyingPayment(false);
        }
    };

    useEffect(() => {
        fetchOrderDetails();
    }, [id]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Map order status to themed badge classes
    const getStatusBadgeClass = (status) => {
        const map = {
            pending: 'badge-theme-yellow',
            accepted: 'badge-theme-blue',
            shipped: 'badge-theme-blue',
            delivered: 'badge-theme-blue',
            cancelled: 'badge-theme-red'
        };
        return map[status] || 'badge-theme-blue';
    };

    const getPaymentBadgeClass = (status) => {
        const map = {
            paid: 'badge-theme-blue',
            pending: 'badge-theme-yellow',
            failed: 'badge-theme-red'
        };
        return map[status] || 'badge-theme-yellow';
    };

    const getAddressText = () => {
        const a = orderData?.address || {};
        const parts = [a.house, a.area, a.landmark, a.city].filter(Boolean);
        return parts.join(', ');
    };

    const getMapsLinks = () => {
        const a = orderData?.address || {};
        const lat = a.latitude || a.lat; // support alternative naming
        const lng = a.longitude || a.lng;
        const hasCoords = lat && lng;
        const encodedAddr = encodeURIComponent(getAddressText());
        const viewUrl = hasCoords
            ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
            : `https://www.google.com/maps/search/?api=1&query=${encodedAddr}`;
        const navUrl = hasCoords
            ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
            : `https://www.google.com/maps/dir/?api=1&destination=${encodedAddr}`;
        return { viewUrl, navUrl, hasCoords, lat, lng };
    };

    const buildShareText = () => {
        const { navUrl } = getMapsLinks();
        const a = orderData?.address || {};
        const itemsCount = orderData?.items?.length || 0;
        const payMethod = (orderData?.paymentMethod || 'cod').toUpperCase();
        const payStatus = (orderData?.paymentStatus || 'pending').toUpperCase();
        const trackUrl = `${window.location.origin}/track-payment/${orderData?._id || ''}`;
        const lines = [
            `Order #${orderData?.orderNumber || orderData?._id || ''}`,
            `Customer: ${a.name || 'N/A'} (${orderData?.mobile || 'N/A'})`,
            `Address: ${[a.house, a.area, a.landmark, a.city].filter(Boolean).join(', ')}`,
            `Items: ${itemsCount}`,
            `Amount: ₹${orderData?.totalAmount || 0}`,
            `Payment: ${payMethod} (${payStatus})`,
            `Track Payment: ${trackUrl}`,
            `Navigate: ${navUrl}`,
        ];
        return lines.join('\n');
    };

    const handleShareSystem = async () => {
        const text = buildShareText();
        if (navigator.share) {
            try {
                await navigator.share({ title: 'Delivery Details', text });
                return;
            } catch {}
        }
        // Fallback to copy if Web Share not available
        try {
            await navigator.clipboard.writeText(text);
            alert('Delivery details copied to clipboard. Paste in your chat app.');
        } catch {
            window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
        }
    };

    const handleShareWhatsApp = async () => {
        // Fetch all delivery partners for WhatsApp sharing
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE_URL}/delivery-partners`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAllDeliveryPartners(response.data || []);
            setShowWhatsAppModal(true);
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleSendWhatsApp = (partner) => {
        const text = buildShareText();
        const phone = partner.phoneNumber.replace(/[^0-9]/g, ''); // Remove any non-numeric characters
        const url = `https://wa.me/91${phone}?text=${encodeURIComponent(text)}`;
        window.open(url, '_blank');
        setShowWhatsAppModal(false);
    };

    const handleCopyDetails = async () => {
        const text = buildShareText();
        try {
            await navigator.clipboard.writeText(text);
            alert('Copied delivery details to clipboard.');
        } catch {
            alert('Copy failed. Long-press to paste this message in your chat app.');
        }
    };

    if (loading) {
        return (
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="section-title">
                            <h4 className="mt-1">ORDER DETAILS</h4>
                            <p className="text-muted p-0 m-0">Manage and modify order information</p>
                        </div>
                    </div>
                </div>
                <AdminPageLoader message="Loading order details..." />
            </div>
        );
    }

    if (!orderData) {
        return (
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="text-center mt-5">
                    <h5 className="text-muted">Order not found</h5>
                    <button className="btn btn-danger mt-3" onClick={() => navigate('/admin/orders')}>
                        Back to Orders
                    </button>
                </div>
            </div>
        );
    }
    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 pb-5 g-0 adma">
                {/* Page Header */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="section-title">
                                <h4 className="mt-1">ORDER DETAILS</h4>
                                <p className="text-muted p-0 m-0">Manage and modify order information</p>
                            </div>
                        </div>
                    </div>
                </div>

                <OrderNavigationStrip activeStatus={orderData.status} refreshTrigger={refreshCounts} />

                {/* Order Action Buttons Section (Themed Squares) */}
                <div className="row dashboard-actions-row mb-4">
                    {/* Back to Orders */}
                    <div>
                        <button
                            className="dashboard-action-card w-100"
                            onClick={() => navigate('/admin/orders')}
                        >
                            <FaArrowLeft className="mb-2" />
                            <span>Back to Orders</span>
                        </button>
                    </div>

                    {/* Accept Order (Dynamic) */}
                    {orderData.status !== 'accepted' && orderData.status !== 'delivered' && orderData.status !== 'cancelled' && (
                        <div>
                            <button
                                className="dashboard-action-card w-100"
                                onClick={() => handleUpdateStatus('accepted')}
                                disabled={updatingStatus === 'accepted'}
                            >
                                {updatingStatus === 'accepted' ? (
                                    <span className="spinner-border spinner-border-sm mb-2" />
                                ) : (
                                    <FaCheckCircle className="mb-2" />
                                )}
                                <span>Accept Order</span>
                            </button>
                        </div>
                    )}

                    {/* Mark as Shipped (Dynamic) */}
                    {orderData.status === 'accepted' && (
                        <div>
                            <button
                                className="dashboard-action-card w-100"
                                onClick={() => handleUpdateStatus('shipped')}
                                disabled={updatingStatus === 'shipped'}
                            >
                                {updatingStatus === 'shipped' ? (
                                    <span className="spinner-border spinner-border-sm mb-2" />
                                ) : (
                                    <FaTruck className="mb-2" />
                                )}
                                <span>Mark as Shipped</span>
                            </button>
                        </div>
                    )}

                    {/* Mark as Delivered (Dynamic) */}
                    {orderData.status === 'shipped' && (
                        <div>
                            <button
                                className="dashboard-action-card w-100"
                                onClick={() => handleUpdateStatus('delivered')}
                                disabled={updatingStatus === 'delivered'}
                            >
                                {updatingStatus === 'delivered' ? (
                                    <span className="spinner-border spinner-border-sm mb-2" />
                                ) : (
                                    <FaCheckCircle className="mb-2" />
                                )}
                                <span>Mark as Delivered</span>
                            </button>
                        </div>
                    )}

                    {/* Cancel Order */}
                    {orderData.status !== 'cancelled' && orderData.status !== 'delivered' && (
                        <div>
                            <button
                                className="dashboard-action-card w-100"
                                onClick={() => handleUpdateStatus('cancelled')}
                                disabled={updatingStatus === 'cancelled'}
                                style={{ color: '#dc3545' }}
                            >
                                {updatingStatus === 'cancelled' ? (
                                    <span className="spinner-border spinner-border-sm mb-2" />
                                ) : (
                                    <FaTimesCircle className="mb-2" style={{ color: '#dc3545' }} />
                                )}
                                <span style={{ color: '#dc3545' }}>Cancel Order</span>
                            </button>
                        </div>
                    )}

                    {/* Mark as Paid */}
                    <div>
                        <button
                            className="dashboard-action-card w-100"
                            onClick={() => handleUpdatePayment('paid')}
                            disabled={orderData.paymentStatus === 'paid' || orderData.status === 'delivered'}
                        >
                            <FaCheckCircle className="mb-2" />
                            <span>Mark as Paid</span>
                        </button>
                    </div>

                    {/* Mark as Pending */}
                    <div>
                        <button
                            className="dashboard-action-card w-100"
                            onClick={() => handleUpdatePayment('pending')}
                            disabled={orderData.paymentStatus === 'pending' || orderData.status === 'delivered'}
                        >
                            <FaTimesCircle className="mb-2" />
                            <span>Mark as Pending</span>
                        </button>
                    </div>

                    {/* Share Details */}
                    <div>
                        <button
                            className="dashboard-action-card w-100"
                            onClick={handleShareSystem}
                        >
                            <FaShareAlt className="mb-2" />
                            <span>Share Details</span>
                        </button>
                    </div>

                    {/* Share via WhatsApp */}
                    <div>
                        <button
                            className="dashboard-action-card w-100"
                            onClick={handleShareWhatsApp}
                        >
                            <FaWhatsapp className="mb-2" />
                            <span>Share WhatsApp</span>
                        </button>
                    </div>

                    {/* Copy Details */}
                    <div>
                        <button
                            className="dashboard-action-card w-100"
                            onClick={handleCopyDetails}
                        >
                            <FaCopy className="mb-2" />
                            <span>Copy Details</span>
                        </button>
                    </div>
                </div>

                {/* Main Content Layout */}
                <div className="row details-content-row">
                    {/* Left Column: Order details & destination */}
                    <div className="col-lg-7 col-md-12 p-0 details-left-panel">
                        {/* Order Information Card */}
                        <div className="card details-flat-card" style={{ borderBottom: '1px solid #d1d5db', boxShadow: 'none' }}>
                            <div className="card-header bg-indigo-light border-0 d-flex justify-content-between align-items-center" style={{ backgroundColor: '#e8eaf6', borderBottom: '1px solid #d1d5db', padding: '12px 24px' }}>
                                <h5 style={{ color: '#1a237e', fontWeight: '700', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.8px', margin: 0 }}>
                                    <FaEdit className="me-2" />
                                    Order Information
                                </h5>
                                <button 
                                    className="btn theme-btn-blue-flat btn-sm rounded-0"
                                    onClick={() => setShowSearch(!showSearch)}
                                    disabled={orderData.status === 'shipped' || orderData.status === 'delivered' || orderData.status === 'cancelled'}
                                    style={{
                                        padding: '6px 16px',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                >
                                    <FaPlus size={11} />
                                    Add Items
                                </button>
                            </div>
                            <div className="card-body">
                                {showSearch && (
                                    <div className="mb-3 position-relative">
                                        <div 
                                            className="input-group rounded-0" 
                                            style={{ 
                                                border: searchFocused ? '1px solid #1a237e' : '1px solid #d1d5db', 
                                                transition: 'border-color 0.2s',
                                                boxShadow: searchFocused ? '0 0 0 1px #1a237e' : 'none'
                                            }}
                                        >
                                            <span className="input-group-text bg-white border-0 py-2 ps-3 pe-2">
                                                <FaSearch className="text-muted" size={13} />
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control border-0 rounded-0 py-2 ps-0 shadow-none"
                                                style={{ fontSize: '13px', color: '#1e293b' }}
                                                placeholder="Search products to add by name..."
                                                value={searchTerm}
                                                onChange={handleSearch}
                                                onFocus={() => setSearchFocused(true)}
                                                onBlur={() => setSearchFocused(false)}
                                            />
                                        </div>
                                        {filteredProducts.length > 0 && (
                                            <div 
                                                className="list-group rounded-0 partner-list-container" 
                                                style={{ 
                                                    position: 'absolute',
                                                    top: '100%',
                                                    left: 0,
                                                    right: 0,
                                                    zIndex: 100,
                                                    maxHeight: '320px', 
                                                    overflowY: 'auto', 
                                                    border: searchFocused ? '1px solid #1a237e' : '1px solid #d1d5db',
                                                    borderTop: 'none',
                                                    backgroundColor: '#ffffff',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                                    marginTop: '3px'
                                                }}
                                            >
                                                {filteredProducts.map(product => {
                                                    const qty = selectedQuantities[product._id] || 1;
                                                    const isOutOfStock = (product.availableStock || 0) <= 0;
                                                    return (
                                                        <div
                                                            key={product._id}
                                                            className="list-group-item d-flex justify-content-between align-items-center border-0 border-bottom rounded-0 py-2 px-3"
                                                            style={{ fontSize: '13px', backgroundColor: '#ffffff' }}
                                                        >
                                                            <div className="text-start" style={{ flex: 1, marginRight: '16px' }}>
                                                                <strong style={{ color: '#0f172a' }}>{product.name}</strong>
                                                                <br />
                                                                <small className="text-muted" style={{ fontSize: '11px' }}>
                                                                    Stock: {product.availableStock || 0} • Unit: {product.quantity} {product.unit} • Price: {formatCurrency(product.price)}
                                                                </small>
                                                            </div>
                                                            <div className="d-flex align-items-center gap-2" style={{ flexShrink: 0 }}>
                                                                {!isOutOfStock ? (
                                                                    <>
                                                                        <div className="d-flex align-items-center border border-secondary-subtle" style={{ height: '30px', backgroundColor: '#f8fafc' }}>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm border-0 rounded-0 px-2 d-flex align-items-center justify-content-center"
                                                                                style={{ height: '100%', fontSize: '14px', color: '#475569', backgroundColor: 'transparent' }}
                                                                                onClick={() => handleDecreaseQuantity(product._id)}
                                                                                disabled={qty <= 1}
                                                                            >
                                                                                -
                                                                            </button>
                                                                            <span 
                                                                                className="px-2 text-center" 
                                                                                style={{ minWidth: '24px', fontSize: '13px', fontWeight: '600', color: '#0f172a' }}
                                                                            >
                                                                                {qty}
                                                                            </span>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm border-0 rounded-0 px-2 d-flex align-items-center justify-content-center"
                                                                                style={{ height: '100%', fontSize: '14px', color: '#475569', backgroundColor: 'transparent' }}
                                                                                onClick={() => handleIncreaseQuantity(product._id, product.availableStock || 0)}
                                                                                disabled={qty >= (product.availableStock || 0)}
                                                                            >
                                                                                +
                                                                            </button>
                                                                        </div>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm text-white rounded-0 px-3 d-flex align-items-center gap-1"
                                                                            style={{ 
                                                                                backgroundColor: '#1a237e', 
                                                                                height: '30px', 
                                                                                fontSize: '12px',
                                                                                fontWeight: '500'
                                                                            }}
                                                                            onClick={() => handleAddItem(product, qty)}
                                                                            disabled={modifyingItem === product._id}
                                                                        >
                                                                            {modifyingItem === product._id ? (
                                                                                <span className="spinner-border spinner-border-sm" style={{ width: '12px', height: '12px' }} />
                                                                            ) : (
                                                                                <>
                                                                                    <FaPlus size={10} /> Add
                                                                                </>
                                                                            )}
                                                                        </button>
                                                                    </>
                                                                ) : (
                                                                    <span className="text-danger fw-medium" style={{ fontSize: '12px' }}>Out of Stock</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="row g-3">
                                    <div className="col-md-6 details-split-border">
                                        <table className="table table-sm table-borderless mb-0">
                                            <tbody>
                                                <tr>
                                                    <td className="text-muted py-1 ps-0" style={{ fontSize: '13px', width: '40%' }}>Order Number:</td>
                                                    <td className="py-1" style={{ fontSize: '13px' }}>
                                                        <strong>{orderData.orderNumber || `best-${orderData._id?.slice(-5)}`}</strong>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted py-1 ps-0" style={{ fontSize: '13px' }}>Order Date:</td>
                                                    <td className="py-1" style={{ fontSize: '13px' }}>
                                                        <strong>{formatDate(orderData.createdAt)}</strong>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted py-1 ps-0" style={{ fontSize: '13px' }}>Mobile:</td>
                                                    <td className="py-1" style={{ fontSize: '13px' }}>
                                                        <strong>{orderData.mobile || 'N/A'}</strong>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted py-1 ps-0" style={{ fontSize: '13px' }}>Merchant Order ID:</td>
                                                    <td className="py-1" style={{ fontSize: '13px' }}>
                                                        {orderData.merchantOrderId ? (
                                                            <div className="d-inline-flex align-items-center gap-2">
                                                                <strong>{orderData.merchantOrderId}</strong>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-secondary rounded-0 py-0 px-2"
                                                                    style={{ height: '20px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                                                    title="Copy Merchant Order ID"
                                                                    onClick={() => {
                                                                        if (navigator?.clipboard?.writeText) {
                                                                            navigator.clipboard.writeText(orderData.merchantOrderId).then(() => {
                                                                                alert('Merchant Order ID copied');
                                                                            }).catch(() => {
                                                                                alert('Copy failed');
                                                                            });
                                                                        } else {
                                                                            const textArea = document.createElement('textarea');
                                                                            textArea.value = orderData.merchantOrderId;
                                                                            document.body.appendChild(textArea);
                                                                            textArea.select();
                                                                            try { document.execCommand('copy'); alert('Merchant Order ID copied'); } catch { alert('Copy failed'); }
                                                                            document.body.removeChild(textArea);
                                                                        }
                                                                    }}
                                                                >
                                                                    <FaCopy size={10} />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <strong className="text-muted">—</strong>
                                                        )}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted py-1 ps-0" style={{ fontSize: '13px' }}>Status:</td>
                                                    <td className="py-1" style={{ fontSize: '13px' }}>
                                                        <span className={`badge rounded-0 ${getStatusBadgeClass(orderData.status)}`} style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '0px' }}>
                                                            {orderData.status?.toUpperCase() || 'PENDING'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    
                                    <div className="col-md-6">
                                        <table className="table table-sm table-borderless mb-0">
                                            <tbody>
                                                <tr>
                                                    <td className="text-muted py-1 ps-0" style={{ fontSize: '13px', width: '45%' }}>Payment Method:</td>
                                                    <td className="py-1" style={{ fontSize: '13px' }}>
                                                        <strong>{(orderData.paymentMethod || 'cod').toUpperCase()}</strong>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted py-1 ps-0" style={{ fontSize: '13px' }}>Payment Status:</td>
                                                    <td className="py-1" style={{ fontSize: '13px' }}>
                                                        <div className="d-flex flex-wrap align-items-center gap-1">
                                                            <span className={`badge rounded-0 ${getPaymentBadgeClass(orderData.paymentStatus)}`} style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '0px' }}>
                                                                {(orderData.paymentStatus || 'pending').toUpperCase()}
                                                            </span>
                                                            {orderData.merchantOrderId && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm theme-btn-outline-blue rounded-0 d-inline-flex align-items-center justify-content-center py-0 px-2"
                                                                    style={{ height: '20px', fontSize: '11px' }}
                                                                    onClick={handleReverifyPayment}
                                                                    title="Re-verify payment with gateway"
                                                                    disabled={reverifyingPayment}
                                                                >
                                                                    {reverifyingPayment && (
                                                                        <span className="spinner-border spinner-border-sm me-1" style={{ width: '10px', height: '10px' }} role="status" aria-hidden="true" />
                                                                    )}
                                                                    <span>{reverifyingPayment ? 'Verifying...' : 'Re-verify'}</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted py-1 ps-0" style={{ fontSize: '13px' }}>Subtotal:</td>
                                                    <td className="py-1" style={{ fontSize: '13px' }}>
                                                        <strong>{formatCurrency(orderData.items?.reduce((acc, item) => acc + (item.price * item.quantity), 0) || 0)}</strong>
                                                    </td>
                                                </tr>
                                                {orderData.appliedOffer && orderData.appliedOffer.cartValue && (
                                                    <tr>
                                                        <td className="text-muted py-1 ps-0" style={{ fontSize: '13px' }}>Applied Offer:</td>
                                                        <td className="py-1" style={{ fontSize: '13px' }}>
                                                            <strong className="text-primary" style={{ fontSize: '12px' }}>
                                                                {orderData.appliedOffer?.name || 'Special Offer'}
                                                            </strong>
                                                        </td>
                                                    </tr>
                                                )}
                                                <tr>
                                                    <td className="text-muted py-1 ps-0" style={{ fontSize: '13px' }}>Delivery Charges:</td>
                                                    <td className="py-1" style={{ fontSize: '13px' }}>
                                                        <strong>{formatCurrency(orderData.deliveryCharge || 0)}</strong>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted py-1 ps-0" style={{ fontSize: '13px' }}>Handling Charges:</td>
                                                    <td className="py-1" style={{ fontSize: '13px' }}>
                                                        <strong>{formatCurrency(orderData.handlingCharge || 0)}</strong>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted py-1 ps-0" style={{ fontSize: '13px' }}>Total Amount:</td>
                                                    <td className="py-1" style={{ fontSize: '14px' }}>
                                                        <strong className="text-success">{formatCurrency(orderData.totalAmount || 0)}</strong>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Delivery & Destination Card */}
                        <div className="card details-flat-card" style={{ borderBottom: orderData.deliveryPartner ? '1px solid #d1d5db' : 'none', boxShadow: 'none' }}>
                            <div className="card-header bg-indigo-light border-0" style={{ backgroundColor: '#e8eaf6', borderBottom: '1px solid #d1d5db', padding: '12px 24px' }}>
                                <h5 style={{ color: '#1a237e', fontWeight: '700', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.8px', margin: 0 }}>
                                    <FaMapMarkerAlt className="me-2" />
                                    Delivery & Destination
                                </h5>
                            </div>
                            <div className="card-body" style={{ padding: '16px 24px' }}>
                                {orderData.address ? (
                                    <>
                                        <h6 className="fw-bold mb-1" style={{ color: '#0f172a', fontSize: '14px' }}>{orderData.address.name}</h6>
                                        <p className="text-secondary mb-2" style={{ fontSize: '13px', lineHeight: '1.45' }}>
                                            {orderData.address.house}, {orderData.address.area}
                                            {orderData.address.landmark && <><br />{orderData.address.landmark}</>}
                                            {orderData.address.city && <><br />{orderData.address.city}</>}
                                            <br />
                                            <span className="text-dark fw-bold">Contact: {orderData.mobile}</span>
                                        </p>
                                    </>
                                ) : (
                                    <p className="text-muted mb-0" style={{ fontSize: '13px' }}>No address available</p>
                                )}
                                {orderData.address && (
                                    (() => {
                                        const { viewUrl, navUrl, hasCoords, lat, lng } = getMapsLinks();
                                        const formattedLat = hasCoords && !isNaN(Number(lat)) ? Number(lat).toFixed(5) : lat;
                                        const formattedLng = hasCoords && !isNaN(Number(lng)) ? Number(lng).toFixed(5) : lng;
                                        return (
                                            <div className="d-flex flex-wrap align-items-center gap-3 mt-2" style={{ fontSize: '13px' }}>
                                                {hasCoords && (
                                                    <span className="text-muted" style={{ fontSize: '12px' }}>Coords: {formattedLat}, {formattedLng}</span>
                                                )}
                                                <a
                                                    href={viewUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-primary text-decoration-none d-inline-flex align-items-center fw-600 gap-1"
                                                    title="Open location in Google Maps"
                                                >
                                                    <FaMapMarkerAlt size={12} /> View Maps
                                                </a>
                                                <a
                                                    href={navUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-success text-decoration-none d-inline-flex align-items-center fw-600 gap-1"
                                                    title="Start navigation in Google Maps"
                                                >
                                                    <FaDirections size={12} /> Navigate
                                                </a>
                                            </div>
                                        );
                                    })()
                                )}
                            </div>
                        </div>

                        {/* Assigned Delivery Partner Card */}
                        {orderData.deliveryPartner && (
                            <div className="card details-flat-card" style={{ boxShadow: 'none' }}>
                                <div className="card-header bg-indigo-light border-0" style={{ backgroundColor: '#e8eaf6', borderBottom: '1px solid #d1d5db', padding: '12px 24px' }}>
                                    <h5 style={{ color: '#1a237e', fontWeight: '700', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.8px', margin: 0 }}>
                                        <FaTruck className="me-2" />
                                        Assigned Delivery Partner
                                    </h5>
                                </div>
                                <div className="card-body p-3">
                                    <div className="d-flex align-items-center">
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '50%',
                                            background: '#e8eaf6',
                                            color: '#1a237e',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: '600',
                                            fontSize: '14px',
                                            marginRight: '12px'
                                        }}>
                                            {orderData.deliveryPartner.name ? orderData.deliveryPartner.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : 'DP'}
                                        </div>
                                        <div>
                                            <strong style={{ color: '#0f172a', fontSize: '14px' }}>{orderData.deliveryPartner.name}</strong><br />
                                            <small className="text-muted" style={{ fontSize: '12px' }}>
                                                Phone: {orderData.deliveryPartner.phoneNumber}
                                            </small>
                                        </div>
                                    </div>
                                    {(orderData.status === 'delivered' || orderData.status === 'cancelled') && (
                                        <div className="mt-3">
                                            <span className="badge" style={{ background: orderData.status === 'delivered' ? '#10b981' : '#ef4444', color: '#fff', fontSize: '11px', padding: '4px 8px', borderRadius: '4px' }}>
                                                {orderData.status === 'delivered' ? 'Delivery Completed' : 'Order Cancelled'}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Column: Order items table */}
                    <div className="col-lg-5 col-md-12 p-0 details-right-panel">
                        <div className="card details-flat-card h-100" style={{ boxShadow: 'none' }}>
                            <div className="card-header bg-indigo-light border-0" style={{ backgroundColor: '#e8eaf6', borderBottom: '1px solid #d1d5db', padding: '12px 24px' }}>
                                <h5 style={{ color: '#1a237e', fontWeight: '700', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.8px', margin: 0 }}>
                                    <FaEye className="me-2" />
                                    Order Items ({orderData.items?.length || 0})
                                </h5>
                            </div>
                            <div className="card-body p-0">
                                {orderData.items?.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table mb-0" style={{ borderCollapse: 'collapse' }}>
                                            <thead style={{ backgroundColor: '#ffffff' }}>
                                                <tr className="align-middle" style={{ fontSize: '13px' }}>
                                                    <th style={{ paddingLeft: '20px', backgroundColor: '#ffffff', color: '#475569', fontWeight: '600', padding: '10px 8px 10px 20px', borderBottom: '1px solid #d1d5db' }}>Item</th>
                                                    <th className="text-center" style={{ backgroundColor: '#ffffff', color: '#475569', fontWeight: '600', padding: '10px 8px', borderBottom: '1px solid #d1d5db' }}>Qty</th>
                                                    <th className="text-end" style={{ backgroundColor: '#ffffff', color: '#475569', fontWeight: '600', padding: '10px 8px', borderBottom: '1px solid #d1d5db' }}>Price</th>
                                                    <th className="text-center" style={{ paddingRight: '20px', backgroundColor: '#ffffff', color: '#475569', fontWeight: '600', padding: '10px 20px 10px 8px', borderBottom: '1px solid #d1d5db' }}>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {orderData.items.map((item, index) => (
                                                    <tr key={`${item._id}-${index}`} className="align-middle" style={{ fontSize: '13px' }}>
                                                        <td style={{ paddingLeft: '20px', padding: '10px 8px 10px 20px', borderBottom: '1px solid #d1d5db' }}>
                                                            <strong>{item.name}</strong>
                                                        </td>
                                                        <td className="text-center" style={{ padding: '10px 8px', borderBottom: '1px solid #d1d5db' }}>
                                                            <div className="d-inline-flex align-items-center justify-content-center border border-secondary-subtle" style={{ height: '26px', backgroundColor: '#f8fafc' }}>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm border-0 rounded-0 px-2 d-flex align-items-center justify-content-center"
                                                                    style={{ height: '100%', fontSize: '12px', color: '#475569', backgroundColor: 'transparent', padding: '0 8px' }}
                                                                    onClick={() => handleUpdateItemQuantity(item, Number(item.quantity) - 1)}
                                                                    disabled={modifyingItem === item._id || Number(item.quantity) <= 1 || orderData.status === 'shipped' || orderData.status === 'delivered' || orderData.status === 'cancelled'}
                                                                >
                                                                    -
                                                                </button>
                                                                <span 
                                                                    className="px-2 text-center d-flex align-items-center justify-content-center" 
                                                                    style={{ minWidth: '20px', fontSize: '12px', fontWeight: '600', color: '#0f172a', height: '100%' }}
                                                                >
                                                                    {modifyingItem === item._id ? (
                                                                        <span className="spinner-border spinner-border-sm" style={{ width: '10px', height: '10px', borderWidth: '1.5px' }} />
                                                                    ) : (
                                                                        item.quantity
                                                                    )}
                                                                </span>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm border-0 rounded-0 px-2 d-flex align-items-center justify-content-center"
                                                                    style={{ height: '100%', fontSize: '12px', color: '#475569', backgroundColor: 'transparent', padding: '0 8px' }}
                                                                    onClick={() => handleUpdateItemQuantity(item, Number(item.quantity) + 1)}
                                                                    disabled={modifyingItem === item._id || orderData.status === 'shipped' || orderData.status === 'delivered' || orderData.status === 'cancelled'}
                                                                >
                                                                    +
                                                                </button>
                                                            </div>
                                                        </td>
                                                        <td className="text-end" style={{ padding: '10px 8px', borderBottom: '1px solid #d1d5db' }}>
                                                            <span>{formatCurrency(item.price)}</span>
                                                        </td>
                                                        <td className="text-center" style={{ paddingRight: '20px', padding: '10px 20px 10px 8px', borderBottom: '1px solid #d1d5db' }}>
                                                            <button
                                                                className="btn btn-link text-danger p-0 d-inline-flex justify-content-center align-items-center"
                                                                style={{ width: '24px', height: '24px', textDecoration: 'none' }}
                                                                onClick={() => handleRemoveItem(item)}
                                                                disabled={modifyingItem === item._id || orderData.status === 'shipped' || orderData.status === 'delivered' || orderData.status === 'cancelled'}
                                                                title="Remove Item"
                                                            >
                                                                {modifyingItem === item._id ? (
                                                                    <span className="spinner-border spinner-border-sm" style={{ width: '12px', height: '12px' }} />
                                                                ) : (
                                                                    <FaTrash size={13} />
                                                                )}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center py-5">
                                        <p className="text-muted m-0">No items in this order</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delivery Partner Selection Modal */}
            {showDeliveryPartnerModal && (
                <div className="assign-modal-overlay">
                    <div className="assign-modal-content">
                        <div className="assign-modal-header">
                            <div className="d-flex align-items-center gap-2">
                                <div style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '8px',
                                    background: '#e8eaf6',
                                    color: '#1a237e',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '15px'
                                }}>
                                    <FaTruck />
                                </div>
                                <div className="d-flex flex-column align-items-start">
                                    <h5 style={{ color: '#0f172a', fontSize: '14px', fontWeight: '700', margin: 0, padding: 0 }}>
                                        Assign Driver
                                    </h5>
                                    <span style={{ color: '#64748b', fontSize: '11px', marginTop: '2px', display: 'block', textAlign: 'left' }}>
                                        Order <strong style={{ color: '#4f46e5', fontWeight: 600 }}>{orderData.orderNumber || `best-${orderData._id?.slice(-5)}`}</strong> • Choose driver
                                    </span>
                                </div>
                            </div>
                            <button 
                                type="button" 
                                onClick={() => {
                                    setShowDeliveryPartnerModal(false);
                                    setSelectedDeliveryPartner('');
                                }}
                                style={{
                                    border: 'none',
                                    background: 'none',
                                    fontSize: '20px',
                                    color: '#94a3b8',
                                    cursor: 'pointer',
                                    padding: '0 2px',
                                    lineHeight: 1,
                                    transition: 'color 0.2s'
                                }}
                                onMouseOver={e => e.currentTarget.style.color = '#0f172a'}
                                onMouseOut={e => e.currentTarget.style.color = '#94a3b8'}
                            >
                                &times;
                            </button>
                        </div>
                        <div className="assign-modal-body">
                            <span style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '1px', color: '#94a3b8', marginBottom: '8px', display: 'block', textAlign: 'left' }}>
                                SELECT AVAILABLE DRIVER
                            </span>
                            {deliveryPartners.length > 0 ? (
                                <>
                                    <div className="partner-list-container" style={{ maxHeight: '220px', overflowY: 'auto', paddingRight: '4px' }}>
                                        {deliveryPartners.map((partner) => {
                                            const isSelected = selectedDeliveryPartner === partner._id;
                                            const isBusy = !partner.isAvailable;
                                            const initials = partner.name
                                                ? partner.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
                                                : 'DP';

                                            return (
                                                <div
                                                    key={partner._id}
                                                    onClick={() => !isBusy && setSelectedDeliveryPartner(partner._id)}
                                                    className={`partner-card ${isSelected ? 'selected' : ''} ${isBusy ? 'busy' : ''}`}
                                                >
                                                    <div style={{
                                                        width: '28px',
                                                        height: '28px',
                                                        borderRadius: '50%',
                                                        background: isSelected ? '#4f46e5' : '#f1f5f9',
                                                        color: isSelected ? '#ffffff' : '#475569',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontWeight: '600',
                                                        fontSize: '11px',
                                                        marginRight: '10px',
                                                        transition: 'all 0.2s'
                                                    }}>
                                                        {initials}
                                                    </div>
                                                    <div style={{ flex: 1, textAlign: 'left' }}>
                                                        <div style={{ fontWeight: '600', color: isBusy ? '#94a3b8' : '#1e293b', fontSize: '13px' }}>
                                                            {partner.name}
                                                        </div>
                                                        <div style={{ color: '#64748b', fontSize: '11px', marginTop: '1px' }}>
                                                            {partner.phoneNumber}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        {isBusy ? (
                                                            <span style={{
                                                                fontSize: '10px',
                                                                fontWeight: '600',
                                                                background: '#f1f5f9',
                                                                color: '#475569',
                                                                padding: '2px 6px',
                                                                borderRadius: '4px',
                                                                display: 'inline-block'
                                                            }}>
                                                                Busy
                                                            </span>
                                                        ) : (
                                                            <span style={{
                                                                fontSize: '10px',
                                                                fontWeight: '600',
                                                                background: '#dcfce7',
                                                                color: '#166534',
                                                                padding: '2px 6px',
                                                                borderRadius: '4px',
                                                                display: 'inline-block'
                                                            }}>
                                                                Available
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            ) : (
                                <div className="text-center py-4">
                                    <div style={{
                                        width: '42px',
                                        height: '42px',
                                        borderRadius: '50%',
                                        background: '#fffbeb',
                                        color: '#d97706',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px',
                                        marginBottom: '12px'
                                    }}>
                                        <FaTruck />
                                    </div>
                                    <h6 className="fw-bold mb-1" style={{ color: '#111827', fontSize: '13px' }}>No Drivers Registered</h6>
                                </div>
                            )}
                        </div>
                        <div className="assign-modal-footer">
                            <button 
                                type="button" 
                                style={{
                                    background: '#ffffff',
                                    color: '#475569',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    padding: '6px 12px',
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    flex: 1,
                                    height: '36px',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setShowDeliveryPartnerModal(false);
                                    setSelectedDeliveryPartner('');
                                }}
                            >
                                Cancel
                            </button>
                            <button 
                                type="button" 
                                style={{
                                    background: selectedDeliveryPartner ? 'linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)' : '#e2e8f0',
                                    color: selectedDeliveryPartner ? '#ffffff' : '#94a3b8',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '6px 12px',
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    flex: 1.5,
                                    height: '36px',
                                    cursor: selectedDeliveryPartner ? 'pointer' : 'not-allowed',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px'
                                }}
                                onClick={handleAssignAndShip}
                                disabled={!selectedDeliveryPartner || updatingStatus === 'shipped'}
                            >
                                {updatingStatus === 'shipped' ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" style={{ width: '14px', height: '14px' }} />
                                        Assigning...
                                    </>
                                ) : (
                                    <>
                                        <FaTruck size={14} />
                                        Assign & Ship
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* WhatsApp Delivery Partner Selection Modal */}
            {showWhatsAppModal && (
                <div className="assign-modal-overlay">
                    <div className="assign-modal-content" style={{ borderColor: '#25D366' }}>
                        <div className="assign-modal-header" style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <div className="d-flex align-items-center gap-2">
                                <div style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '8px',
                                    background: '#e6fbe8',
                                    color: '#25D366',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '15px'
                                }}>
                                    <FaWhatsapp />
                                </div>
                                <div className="d-flex flex-column align-items-start">
                                    <h5 style={{ color: '#0f172a', fontSize: '14px', fontWeight: '700', margin: 0, padding: 0 }}>
                                        Share via WhatsApp
                                    </h5>
                                    <span style={{ color: '#64748b', fontSize: '11px', marginTop: '2px', display: 'block', textAlign: 'left' }}>
                                        Choose driver to send order details
                                    </span>
                                </div>
                            </div>
                            <button 
                                type="button" 
                                onClick={() => setShowWhatsAppModal(false)}
                                style={{
                                    border: 'none',
                                    background: 'none',
                                    fontSize: '20px',
                                    color: '#94a3b8',
                                    cursor: 'pointer',
                                    padding: '0 2px',
                                    lineHeight: 1,
                                    transition: 'color 0.2s'
                                }}
                                onMouseOver={e => e.currentTarget.style.color = '#0f172a'}
                                onMouseOut={e => e.currentTarget.style.color = '#94a3b8'}
                            >
                                &times;
                            </button>
                        </div>
                        <div className="assign-modal-body">
                            <span style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '1px', color: '#94a3b8', marginBottom: '8px', display: 'block', textAlign: 'left' }}>
                                SELECT DRIVER
                            </span>
                            {allDeliveryPartners.length > 0 ? (
                                <div className="partner-list-container" style={{ maxHeight: '220px', overflowY: 'auto', paddingRight: '4px' }}>
                                    {allDeliveryPartners.map((partner) => {
                                        const initials = partner.name
                                            ? partner.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
                                            : 'DP';
                                        return (
                                            <div
                                                key={partner._id}
                                                onClick={() => handleSendWhatsApp(partner)}
                                                className="partner-card"
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div style={{
                                                    width: '28px',
                                                    height: '28px',
                                                    borderRadius: '50%',
                                                    background: '#e6fbe8',
                                                    color: '#25D366',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: '600',
                                                    fontSize: '11px',
                                                    marginRight: '10px'
                                                }}>
                                                    {initials}
                                                </div>
                                                <div style={{ flex: 1, textAlign: 'left' }}>
                                                    <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '13px' }}>
                                                        {partner.name}
                                                    </div>
                                                    <div style={{ color: '#64748b', fontSize: '11px', marginTop: '1px' }}>
                                                        {partner.phoneNumber}
                                                    </div>
                                                </div>
                                                <div>
                                                    <FaWhatsapp size={18} color="#25D366" />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <div style={{
                                        width: '42px',
                                        height: '42px',
                                        borderRadius: '50%',
                                        background: '#e6fbe8',
                                        color: '#25D366',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px',
                                        marginBottom: '12px'
                                    }}>
                                        <FaWhatsapp />
                                    </div>
                                    <h6 className="fw-bold mb-1" style={{ color: '#111827', fontSize: '13px' }}>No Drivers Registered</h6>
                                </div>
                            )}
                        </div>
                        <div className="assign-modal-footer">
                            <button 
                                type="button" 
                                style={{
                                    background: '#ffffff',
                                    color: '#475569',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    padding: '6px 12px',
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    flex: 1,
                                    height: '36px',
                                    cursor: 'pointer'
                                }}
                                onClick={() => setShowWhatsAppModal(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
}

export default AdminOrderDetailsPage;